import { apiClient, type Paginated } from '@/core/api';
import { resolveContentType } from '@/features/comments/api';
import type {
  Allocation,
  AllocationInput,
  Cycle,
  CycleInput,
  Label,
  LabelInput,
  Milestone,
  MilestoneInput,
  Module,
  ModuleInput,
  Project,
  ProjectBudget,
  ProjectInput,
  ProjectLabel,
  ProjectLabelInput,
  ProjectMember,
  ProjectMemberInput,
  ProjectProgress,
  ProjectView,
  ProjectViewInput,
  State,
  StateInput,
  TimeEntry,
  TimeEntryInput,
  WorkItem,
  WorkItemInput,
  WorkItemRelation,
  WorkItemRelationInput,
  WorkItemType,
  WorkItemTypeInput,
} from './types';

// Note: the module is mounted at /projects, the resource router adds /projects.
export const BASE = '/projects/projects/';
const MILESTONES = '/projects/milestones/';
const TIME_ENTRIES = '/projects/time-entries/';
const ALLOCATIONS = '/projects/allocations/';
const PROJECT_MEMBERS = '/projects/project-members/';
const PROJECT_LABELS = '/projects/project-labels/';
const STATES = '/projects/states/';
const LABELS = '/projects/labels/';
const WORK_ITEM_TYPES = '/projects/work-item-types/';
const WORK_ITEMS = '/projects/work-items/';
const WORK_ITEM_RELATIONS = '/projects/work-item-relations/';
const MODULES = '/projects/modules/';
const CYCLES = '/projects/cycles/';
const VIEWS = '/projects/views/';
const PAGES = '/projects/pages/';
const WORKLOGS = '/projects/worklogs/';

export interface ProjectListParams {
  page?: number;
  search?: string;
  status?: string;
  visibility?: string;
  priority?: string;
  company?: number;
  is_archived?: boolean;
  /** DRF `OrderingFilter` value, e.g. `name` or `-created`. */
  ordering?: string;
}

export async function listProjects(params: ProjectListParams = {}): Promise<Paginated<Project>> {
  const { data } = await apiClient.get<Paginated<Project>>(BASE, { params });
  return data;
}

export async function getProject(id: string | number): Promise<Project> {
  const { data } = await apiClient.get<Project>(`${BASE}${id}/`);
  return data;
}

export async function createProject(input: ProjectInput): Promise<Project> {
  const { data } = await apiClient.post<Project>(BASE, input);
  return data;
}

export async function updateProject(
  id: string | number,
  input: Partial<ProjectInput>,
): Promise<Project> {
  const { data } = await apiClient.patch<Project>(`${BASE}${id}/`, input);
  return data;
}

export async function deleteProject(id: string | number): Promise<void> {
  await apiClient.delete(`${BASE}${id}/`);
}

export async function createMilestone(input: MilestoneInput): Promise<Milestone> {
  const { data } = await apiClient.post<Milestone>(MILESTONES, input);
  return data;
}

export interface MilestoneListParams {
  page?: number;
  project?: number;
  status?: string;
}

export async function listMilestones(params: MilestoneListParams = {}): Promise<Paginated<Milestone>> {
  const { data } = await apiClient.get<Paginated<Milestone>>(MILESTONES, { params });
  return data;
}

export async function updateMilestone(id: number, input: Partial<MilestoneInput>): Promise<Milestone> {
  const { data } = await apiClient.patch<Milestone>(`${MILESTONES}${id}/`, input);
  return data;
}

export async function deleteMilestone(id: number): Promise<void> {
  await apiClient.delete(`${MILESTONES}${id}/`);
}

// ---- Project budget vs actual ----
export async function getProjectBudget(id: number): Promise<ProjectBudget> {
  const { data } = await apiClient.get<ProjectBudget>(`${BASE}${id}/budget/`);
  return data;
}

export async function getProjectProgress(id: string | number): Promise<ProjectProgress> {
  const { data } = await apiClient.get<ProjectProgress>(`${BASE}${id}/progress/`);
  return data;
}

export async function archiveProject(id: string | number): Promise<Project> {
  const { data } = await apiClient.post<Project>(`${BASE}${id}/archive/`);
  return data;
}

export async function restoreProject(id: string | number): Promise<Project> {
  const { data } = await apiClient.post<Project>(`${BASE}${id}/restore/`);
  return data;
}

/** Upload a cover image (multipart) — axios sets the multipart boundary. */
export async function uploadProjectCover(id: string | number, file: File): Promise<Project> {
  const form = new FormData();
  form.append('cover', file);
  const { data } = await apiClient.patch<Project>(`${BASE}${id}/`, form);
  return data;
}

// ---------------------------------------------------------------------------
// Native projects (ADR-071): config, work items, planning
// ---------------------------------------------------------------------------
function crud<T, TInput>(base: string) {
  return {
    list: async (params: Record<string, unknown> = {}): Promise<Paginated<T>> =>
      (await apiClient.get<Paginated<T>>(base, { params })).data,
    get: async (id: string | number): Promise<T> =>
      (await apiClient.get<T>(`${base}${id}/`)).data,
    create: async (input: TInput): Promise<T> =>
      (await apiClient.post<T>(base, input)).data,
    update: async (id: string | number, input: Partial<TInput>): Promise<T> =>
      (await apiClient.patch<T>(`${base}${id}/`, input)).data,
    remove: async (id: string | number): Promise<void> => {
      await apiClient.delete(`${base}${id}/`);
    },
  };
}

export const projectMembersApi = crud<ProjectMember, ProjectMemberInput>(PROJECT_MEMBERS);
export const projectLabelsApi = crud<ProjectLabel, ProjectLabelInput>(PROJECT_LABELS);
export const statesApi = crud<State, StateInput>(STATES);
export const labelsApi = crud<Label, LabelInput>(LABELS);
export const workItemTypesApi = crud<WorkItemType, WorkItemTypeInput>(WORK_ITEM_TYPES);
export const modulesApi = crud<Module, ModuleInput>(MODULES);
export const cyclesApi = crud<Cycle, CycleInput>(CYCLES);
export const projectViewsApi = crud<ProjectView, ProjectViewInput>(VIEWS);
export const workItemRelationsApi = crud<WorkItemRelation, WorkItemRelationInput>(WORK_ITEM_RELATIONS);

const pagesCrud = crud<import('./types').WikiPage, import('./types').WikiPageInput>(PAGES);
export const pagesApi = {
  ...pagesCrud,
  versions: async (id: string | number): Promise<import('./types').WikiPageVersion[]> =>
    (await apiClient.get<import('./types').WikiPageVersion[]>(`${PAGES}${id}/versions/`)).data,
  restoreVersion: async (id: string | number, version: number): Promise<import('./types').WikiPage> =>
    (await apiClient.post<import('./types').WikiPage>(`${PAGES}${id}/restore-version/`, { version })).data,
  publish: async (id: string | number): Promise<import('./types').WikiPage> =>
    (await apiClient.post<import('./types').WikiPage>(`${PAGES}${id}/publish/`)).data,
  unpublish: async (id: string | number): Promise<import('./types').WikiPage> =>
    (await apiClient.post<import('./types').WikiPage>(`${PAGES}${id}/unpublish/`)).data,
  archive: async (id: string | number): Promise<import('./types').WikiPage> =>
    (await apiClient.post<import('./types').WikiPage>(`${PAGES}${id}/archive/`)).data,
  restore: async (id: string | number): Promise<import('./types').WikiPage> =>
    (await apiClient.post<import('./types').WikiPage>(`${PAGES}${id}/restore/`)).data,
  /** A page + its descendant subtree for the editor's page-embed (ADR-074). */
  subtree: async (
    id: string | number,
  ): Promise<{ pages: (import('./types').WikiPage & { depth: number })[]; truncated: boolean }> =>
    (
      await apiClient.get<{
        pages: (import('./types').WikiPage & { depth: number })[];
        truncated: boolean;
      }>(`${PAGES}${id}/subtree/`)
    ).data,
  /** Atomic drag-and-drop reorder/re-parent (one transaction; ADR-080). */
  reorder: async (
    items: { id: number; parent: number | null; order: number; visibility?: string }[],
  ): Promise<import('./types').WikiPage[]> =>
    (await apiClient.post<import('./types').WikiPage[]>(`${PAGES}reorder/`, { items })).data,
};

const workItemsCrud = crud<WorkItem, WorkItemInput>(WORK_ITEMS);
export const workItemsApi = {
  ...workItemsCrud,
  setState: async (id: string | number, state: number): Promise<WorkItem> =>
    (await apiClient.post<WorkItem>(`${WORK_ITEMS}${id}/set-state/`, { state })).data,
  move: async (
    id: string | number,
    body: { sort_order?: string; state?: number },
  ): Promise<WorkItem> => (await apiClient.post<WorkItem>(`${WORK_ITEMS}${id}/move/`, body)).data,
  startTimer: async (
    id: string | number,
    // `previous_description`: the note for the running timer this one replaces.
    body: { description?: string; billable?: boolean; previous_description?: string } = {},
  ): Promise<import('./types').TimerSession> =>
    (await apiClient.post<import('./types').TimerSession>(`${WORK_ITEMS}${id}/timer/start/`, body)).data,
};

// ---- Work-item timer (ADR-076) ----
const TIMER = '/projects/timer/';

export async function getCurrentTimer(): Promise<import('./types').TimerSession | null> {
  const { data } = await apiClient.get<import('./types').TimerSession | null>(`${TIMER}current/`);
  return data;
}

/** `description` — what was done; blank keeps the note given at start (BE-ADR-109). */
export async function stopTimer(body: { description?: string } = {}): Promise<TimeEntry> {
  const { data } = await apiClient.post<TimeEntry>(`${TIMER}stop/`, body);
  return data;
}

export async function discardTimer(): Promise<void> {
  await apiClient.post(`${TIMER}discard/`);
}

// ---- Project attachments (ADR-078) ----
const ATTACHMENTS = '/projects/attachments/';

export async function listProjectAttachments(
  projectId: number,
): Promise<Paginated<import('./types').ProjectAttachment>> {
  const { data } = await apiClient.get<Paginated<import('./types').ProjectAttachment>>(ATTACHMENTS, {
    params: { project: projectId },
  });
  return data;
}

export async function uploadProjectAttachment(
  projectId: number,
  file: File,
): Promise<import('./types').ProjectAttachment> {
  const form = new FormData();
  form.append('project', String(projectId));
  form.append('file', file);
  form.append('name', file.name);
  const { data } = await apiClient.post<import('./types').ProjectAttachment>(ATTACHMENTS, form);
  return data;
}

export async function deleteProjectAttachment(publicId: string): Promise<void> {
  await apiClient.delete(`${ATTACHMENTS}${publicId}/`);
}

// ---- Inline images (work-item rich descriptions) ----
const INLINE_IMAGES = '/projects/inline-images/';

/** Upload an image embedded in a work item's description; returns its embeddable
 *  URL. Kept apart from attachments so inline media never hits the Attachments tab. */
export async function uploadInlineImage(projectId: number, file: File): Promise<string> {
  const form = new FormData();
  form.append('project', String(projectId));
  form.append('file', file);
  const { data } = await apiClient.post<{ public_id: string; file_url: string }>(
    INLINE_IMAGES,
    form,
  );
  return data.file_url;
}

export async function fetchProjectAttachmentBlob(publicId: string): Promise<Blob> {
  const { data } = await apiClient.get<Blob>(`${ATTACHMENTS}${publicId}/download/`, {
    responseType: 'blob',
  });
  return data;
}

// ---- Worklogs report (cross-project timesheets) ----
export interface WorklogListParams {
  page?: number;
  user?: number;
  project?: number;
  /** Several projects at once, comma-separated ids (an invoice's projects). */
  projects?: string;
  work_item?: number;
  billable?: boolean;
  /** false = not billed by any client invoice yet (DEV13-77). */
  billed?: boolean;
  /** Exactly the worklogs this invoice (id) billed (DEV13-77). */
  billed_by?: number;
  date_from?: string;
  date_to?: string;
  ordering?: string;
}

export async function listWorklogs(params: WorklogListParams = {}): Promise<Paginated<TimeEntry>> {
  const { data } = await apiClient.get<Paginated<TimeEntry>>(WORKLOGS, { params });
  return data;
}

/** Server-side aggregate over the FULL filtered set — the list endpoint is
 * paginated, so totals must never be derived by summing a page of results. */
export interface WorklogSummaryUser {
  user: number | null;
  user_name: string | null;
  entries: number;
  minutes: number;
}

/** Dimensions the summary can subtotal by (DEV13-61). */
export type WorklogGroupBy = 'user' | 'project' | 'work_item' | 'date';

/** One subtotal row — `key` is the user/project/work item id or the ISO date
 *  (null for time with no user / work item). */
export interface WorklogSummaryGroup {
  key: number | string | null;
  label: string;
  entries: number;
  minutes: number;
}

export interface WorklogSummary {
  total_entries: number;
  total_minutes: number;
  users: WorklogSummaryUser[];
  group_by: WorklogGroupBy;
  groups: WorklogSummaryGroup[];
}

export type WorklogSummaryParams = Omit<WorklogListParams, 'page' | 'ordering'> & {
  group_by?: WorklogGroupBy;
};

export async function getWorklogSummary(
  params: WorklogSummaryParams = {},
): Promise<WorklogSummary> {
  const { data } = await apiClient.get<WorklogSummary>(`${WORKLOGS}summary/`, { params });
  return data;
}

// ---- Time entries ----
export interface TimeEntryListParams {
  page?: number;
  project?: number;
  user?: number;
  billable?: boolean;
  date_from?: string;
  date_to?: string;
}

export async function listTimeEntries(params: TimeEntryListParams = {}): Promise<Paginated<TimeEntry>> {
  const { data } = await apiClient.get<Paginated<TimeEntry>>(TIME_ENTRIES, { params });
  return data;
}

export async function createTimeEntry(input: TimeEntryInput): Promise<TimeEntry> {
  const { data } = await apiClient.post<TimeEntry>(TIME_ENTRIES, input);
  return data;
}

export async function updateTimeEntry(id: number, input: Partial<TimeEntryInput>): Promise<TimeEntry> {
  const { data } = await apiClient.patch<TimeEntry>(`${TIME_ENTRIES}${id}/`, input);
  return data;
}

export async function deleteTimeEntry(id: number): Promise<void> {
  await apiClient.delete(`${TIME_ENTRIES}${id}/`);
}

// ---- Activity feed (collab.Activity, scoped to a project) ----
let projectContentTypeId: number | null = null;

async function projectsContentType(): Promise<number> {
  if (projectContentTypeId == null) {
    projectContentTypeId = (await resolveContentType('projects.project')).id;
  }
  return projectContentTypeId;
}

export async function listProjectActivity(
  projectId: string | number,
  page = 1,
): Promise<Paginated<import('./types').ProjectActivity>> {
  const ct = await projectsContentType();
  const { data } = await apiClient.get<Paginated<import('./types').ProjectActivity>>(
    '/collab/activity/',
    { params: { content_type: ct, object_id: String(projectId), page } },
  );
  return data;
}

// ---- Allocations ----
export interface AllocationListParams {
  page?: number;
  project?: number;
  user?: number;
}

export async function listAllocations(params: AllocationListParams = {}): Promise<Paginated<Allocation>> {
  const { data } = await apiClient.get<Paginated<Allocation>>(ALLOCATIONS, { params });
  return data;
}

// ---- Resource-allocation utilization (capacity analytics, ADR-072) ----
export interface AllocationUtilizationEntry {
  project: number;
  project_name: string;
  role: string;
  hours_per_week: string;
}

export interface PersonUtilization {
  user: number;
  name: string;
  capacity: string;
  allocated: string;
  utilization_pct: number;
  over_by: string;
  over_allocated: boolean;
  by_project: AllocationUtilizationEntry[];
}

export interface AllocationUtilization {
  as_of: string;
  people: PersonUtilization[];
}

export async function getAllocationUtilization(asOf?: string): Promise<AllocationUtilization> {
  const { data } = await apiClient.get<AllocationUtilization>(`${ALLOCATIONS}utilization/`, {
    params: asOf ? { as_of: asOf } : {},
  });
  return data;
}

export async function createAllocation(input: AllocationInput): Promise<Allocation> {
  const { data } = await apiClient.post<Allocation>(ALLOCATIONS, input);
  return data;
}

export async function updateAllocation(id: number, input: Partial<AllocationInput>): Promise<Allocation> {
  const { data } = await apiClient.patch<Allocation>(`${ALLOCATIONS}${id}/`, input);
  return data;
}

export async function deleteAllocation(id: number): Promise<void> {
  await apiClient.delete(`${ALLOCATIONS}${id}/`);
}
